import {
  setDebug,
  themeParams,
  initData,
  viewport,
  init as initSDK,
  miniApp,
  backButton,
  swipeBehavior,
} from '@tma.js/sdk-react';

interface InitOptions {
  debug: boolean;
  /** Load Eruda mobile dev console (iOS/Android only, dev builds only) */
  eruda?: boolean;
  /** reserved — Telegram-for-macOS theme workaround can be added later if needed */
  mockForMacOS?: boolean;
}

interface TgWebApp {
  setBackgroundColor?: (color: string) => void;
  setHeaderColor?: (color: string) => void;
  setBottomBarColor?: (color: string) => void;
  isVersionAtLeast?: (version: string) => boolean;
}

/**
 * Paints the *native* Telegram chrome (header bar, the background behind the
 * WebView, and the bottom bar) with an explicit RGB colour so there's no
 * off-black / white framing around the Mini App. Without this, telegram-web-app.js
 * defaults the bottom bar to white and binds the header to the user's Telegram
 * theme `bg_color` (white on light themes).
 *
 * Driven through the canonical `window.Telegram.WebApp` global (guaranteed
 * loaded via the script tag in index.html) rather than the @tma.js wrappers,
 * whose `isAvailable()` checks are conservative across client versions. Hex
 * header/bottom-bar colours need Bot API 7.10+; older clients only accept a
 * theme key for the header (`bg_color` matches the surface we just set) and
 * have no bottom bar. Best-effort + try/catch: no-ops outside Telegram. The
 * theme engine (`lib/appearance.ts`) re-calls this with the active theme bg.
 */
export function setTelegramChrome(color: string): void {
  // Preferred: @tma.js wrappers — correct per-client version detection, guarded
  // by isAvailable()/supports('rgb') so they no-op cleanly when unsupported.
  try {
    if (miniApp.setBgColor.isAvailable()) miniApp.setBgColor(color);
  } catch {
    /* noop */
  }
  try {
    if (miniApp.setBottomBarColor.isAvailable()) miniApp.setBottomBarColor(color);
  } catch {
    /* noop */
  }
  try {
    if (miniApp.setHeaderColor.isAvailable() && miniApp.setHeaderColor.supports('rgb')) {
      miniApp.setHeaderColor(color);
    }
  } catch {
    /* noop */
  }

  // Fallback: the telegram-web-app.js global owns the white-bottom-bar /
  // bg_color-header defaults; override it directly for clients where the
  // wrappers above stay unavailable. Hex needs Bot API 7.10+; older clients
  // only accept a theme key for the header (bg_color matches the surface).
  try {
    const wa = (window as unknown as { Telegram?: { WebApp?: TgWebApp } }).Telegram?.WebApp;
    if (wa) {
      const ok710 = typeof wa.isVersionAtLeast === 'function' && wa.isVersionAtLeast('7.10');
      wa.setBackgroundColor?.(color);
      if (ok710) {
        wa.setHeaderColor?.(color);
        wa.setBottomBarColor?.(color);
      } else {
        wa.setHeaderColor?.('bg_color');
      }
    }
  } catch {
    /* best-effort — no-op outside Telegram / on unsupported clients */
  }
}

/**
 * Initializes the Telegram SDK and mounts all components used by the app.
 * Mirrors the official @tma.js/sdk-react template init flow.
 */
export async function initTelegram(options: InitOptions): Promise<void> {
  setDebug(options.debug);
  initSDK();

  if (options.eruda) {
    try {
      const { default: eruda } = await import('eruda');
      eruda.init();
      console.info('[telegram] Eruda dev console initialized');
    } catch (err) {
      console.warn('[telegram] failed to init Eruda:', err);
    }
  }

  backButton.mount.ifAvailable();
  initData.restore();

  if (miniApp.mount.isAvailable()) {
    themeParams.mount();
    miniApp.mount();
    themeParams.bindCssVars();
    // Baseline: black native chrome. The theme engine's one-shot apply in
    // main.tsx immediately re-paints this with the user's active theme bg.
    setTelegramChrome('#000000');
  }

  // Expand the WebView to full height + lock the vertical swipe-to-close
  // gesture. Without disableVertical(), pulling down inside scrollable
  // content can dismiss the Mini App by mistake (Bot API 7.7+; older
  // clients silently no-op).
  if (viewport.mount.isAvailable()) {
    void viewport.mount().then(() => {
      viewport.bindCssVars();
      if (viewport.expand.isAvailable()) viewport.expand();
    });
  }

  if (swipeBehavior.mount.isAvailable()) {
    swipeBehavior.mount();
    if (swipeBehavior.disableVertical.isAvailable()) {
      swipeBehavior.disableVertical();
    }
  }
}
